import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Code, Copy, ChevronRight, Check, Zap, Clock, Shield,
  BookOpen, Sparkles, Terminal, Cpu, Server, Database,
  ArrowUpRight, Play, BookMarked, GitBranch, Rocket,
  Cloud, CpuIcon, Globe, ShieldCheck, Search, Filter,
  Download, Share2, BookText, Layers, ShieldAlert,
  TrendingUp, Users, FileCode, Settings, HelpCircle,
  ExternalLink, Menu, X, Star, ThumbsUp, MessageCircle,
  Eye, EyeOff, TerminalSquare, TestTube, Network,
  Bot, Smartphone, Laptop, DatabaseIcon, Webhook,
  BarChart3, GitPullRequest, Key, Hash, FileJson,
  Lock, Unlock, Wifi, WifiOff, CloudRain, Coffee
} from 'lucide-react';

const Documentation = () => {
      useEffect(() => {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }, []);

  const [activeTab, setActiveTab] = useState('ruc-dni');
  const [copiedCode, setCopiedCode] = useState(null);
  const [hoveredTab, setHoveredTab] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [activeSection, setActiveSection] = useState('implementation');
  const [darkMode, setDarkMode] = useState(false);
  const [apiKeyVisible, setApiKeyVisible] = useState(false);
  const [activeLanguage, setActiveLanguage] = useState('javascript');
  const [viewMode, setViewMode] = useState('documentation');
  const [isTesting, setIsTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const codeRefs = useRef({});

  // Simular datos de monitoreo en tiempo real
  const [monitoringData, setMonitoringData] = useState({
    requestsPerSecond: 42,
    successRate: 99.92,
    activeConnections: 1250,
    averageResponseTime: 156
  });

  useEffect(() => {
    const interval = setInterval(() => {
      setMonitoringData(prev => ({
        requestsPerSecond: Math.floor(40 + Math.random() * 10),
        successRate: 99.9 + Math.random() * 0.1,
        activeConnections: 1200 + Math.floor(Math.random() * 100),
        averageResponseTime: 150 + Math.floor(Math.random() * 20)
      }));
    }, 2000);

    return () => clearInterval(interval);
  }, []);

  const copyToClipboard = (code, tabId) => {
    navigator.clipboard.writeText(code);
    setCopiedCode(tabId);
    setTimeout(() => setCopiedCode(null), 2000);
  };

  const runCodeExample = async (tabId) => {
    console.log(`Ejecutando ejemplo para: ${tabId}`);

    const mockResponse = {
      status: 'success',
      executionTime: '245ms',
      output: `✅ Ejemplo ejecutado correctamente\n📊 Response: 200 OK\n⏱️ Tiempo: 245ms`
    };

    return mockResponse;
  };

  const testEndpoint = async (endpoint) => {
    setIsTesting(true);

    // Simular delay de testing
    await new Promise(resolve => setTimeout(resolve, 2000));

    const mockResult = {
      status: 200,
      responseTime: '156ms',
      data: {
        estado: 'ACTIVO',
        nombre: 'EMPRESA EJEMPLO SAC',
        direccion: 'AV. EJEMPLO 123',
        condicion: 'HABIDO'
      },
      headers: {
        'content-type': 'application/json',
        'x-ratelimit-remaining': '95'
      }
    };

    setTestResult(mockResult);
    setIsTesting(false);
    return mockResult;
  };

  const languages = {
    javascript: { name: 'JavaScript', icon: FileJson, color: 'yellow' },
    python: { name: 'Python', icon: TerminalSquare, color: 'blue' },
    curl: { name: 'cURL', icon: Terminal, color: 'green' },
    java: { name: 'Java', icon: Coffee, color: 'orange' }
  };

  const docs = {
    'ruc-dni': {
      title: 'Consulta RUC/DNI',
      description: 'Valida contribuyentes SUNAT en tiempo real con respuesta en <200ms',
      icon: ShieldCheck,
      color: 'emerald',
      category: 'Validación',
      version: 'v2.5',
      lastUpdated: '2024-01-15',
      status: 'stable',
      usage: '100K/mes',
      steps: [
        'Obtén tu API Key gratuita en el dashboard',
        'Configura headers de autenticación',
        'Realiza GET request con parámetros',
        'Procesa respuesta JSON estructurada'
      ],
      codeExamples: {
        javascript: `// 🌟 Consulta RUC en tiempo real
const validateRUC = async (rucNumber) => {
  try {
    const response = await fetch(
      \`https://api.tudominio.com/v1/ruc-dni?numero=\${rucNumber}&tipo=ruc\`, 
      {
        method: 'GET',
        headers: {
          'Authorization': 'Bearer sk_live_tu_api_key_2025',
          'X-API-Version': '2025-01',
          'Content-Type': 'application/json'
        }
      }
    );
    
    if (!response.ok) throw new Error('Error en la consulta');
    
    const data = await response.json();
    
    return {
      success: data.estado === 'ACTIVO',
      contribuyente: data.nombre,
      direccion: data.direccion,
      condicion: data.condicion,
      timestamp: new Date().toISOString()
    };
  } catch (error) {
    console.error('🚨 Error en consulta RUC:', error);
    throw error;
  }
};`,
        python: `# Consulta RUC en tiempo real
import requests

def validate_ruc(ruc_number):
    headers = {
        'Authorization': 'Bearer sk_live_tu_api_key_2025',
        'X-API-Version': '2025-01',
        'Content-Type': 'application/json'
    }
    
    try:
        response = requests.get(
            f'https://api.tudominio.com/v1/ruc-dni?numero={ruc_number}&tipo=ruc',
            headers=headers
        )
        response.raise_for_status()
        data = response.json()
        
        return {
            'success': data.get('estado') == 'ACTIVO',
            'contribuyente': data.get('nombre'),
            'direccion': data.get('direccion'),
            'condicion': data.get('condicion')
        }
    except requests.exceptions.RequestException as e:
        print(f'🚨 Error en consulta RUC: {e}')
        raise`,
        curl: `curl -X GET \\
  'https://api.tudominio.com/v1/ruc-dni?numero=20512345678&tipo=ruc' \\
  -H 'Authorization: Bearer sk_live_tu_api_key_2025' \\
  -H 'X-API-Version: 2025-01' \\
  -H 'Content-Type: 'application/json'`,
        java: `// Consulta RUC en tiempo real - Java
import java.net.http.*;
import java.net.URI;
import com.fasterxml.jackson.databind.ObjectMapper;

public class RUCValidator {
    private static final String API_URL = "https://api.tudominio.com/v1/ruc-dni";
    
    public static ValidationResult validateRUC(String rucNumber) throws Exception {
        HttpClient client = HttpClient.newHttpClient();
        
        HttpRequest request = HttpRequest.newBuilder()
            .uri(URI.create(API_URL + "?numero=" + rucNumber + "&tipo=ruc"))
            .header("Authorization", "Bearer sk_live_tu_api_key_2025")
            .header("X-API-Version", "2025-01")
            .header("Content-Type", "application/json")
            .GET()
            .build();
            
        HttpResponse<String> response = client.send(request, 
            HttpResponse.BodyHandlers.ofString());
            
        ObjectMapper mapper = new ObjectMapper();
        RUCResponse data = mapper.readValue(response.body(), RUCResponse.class);
        
        return new ValidationResult(
            "ACTIVO".equals(data.getEstado()),
            data.getNombre(),
            data.getDireccion(),
            data.getCondicion()
        );
    }
}`
      },
      responseTime: '< 200ms',
      complexity: 'Beginner',
      successRate: '99.9%',
      parameters: [
        { name: 'numero', type: 'string', required: true, description: 'Número de RUC o DNI' },
        { name: 'tipo', type: 'string', required: true, description: 'Tipo de documento: ruc/dni' }
      ],
      response: {
        estado: 'ACTIVO | INACTIVO',
        nombre: 'string',
        direccion: 'string',
        condicion: 'HABIDO | NO HABIDO',
        ubigeo: 'string'
      },
      testing: {
        method: 'GET',
        endpoint: '/v1/ruc-dni',
        sampleRequest: {
          numero: '20512345678',
          tipo: 'ruc'
        }
      }
    },
    'envio-facturas': {
      title: 'Envío Masivo Facturas',
      description: 'Envía lotes de documentos a SUNAT con manejo de errores inteligente',
      icon: Rocket,
      color: 'blue',
      category: 'Comprobantes',
      version: 'v3.1',
      lastUpdated: '2024-01-10',
      status: 'stable',
      usage: '50K/mes',
      steps: [
        'Prepara documentos en formato XML válido',
        'Configura endpoints batch processing',
        'Maneja respuestas asíncronas',
        'Implementa reintentos automáticos'
      ],
      codeExamples: {
        javascript: `// ⚡ Envío masivo optimizado
const enviarLoteFacturas = async (documentos) => {
  const lote = {
    operacion: 'generar_comprobantes',
    documentos: documentos,
    config: {
      modo: 'produccion',
      reintentos: 3,
      notificacion_webhook: true,
      webhook_url: 'https://tudominio.com/webhook'
    }
  };

  try {
    const response = await fetch('https://api.tudominio.com/v1/envio-masivo', {
      method: 'POST',
      headers: {
        'Authorization': 'Bearer sk_live_tu_api_key_2025',
        'X-Batch-ID': generateBatchId(),
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(lote)
    });

    const { ticket, documentos_procesados, errores } = await response.json();
    
    return {
      ticket,
      exitosos: documentos_procesados,
      errores: errores || []
    };
  } catch (error) {
    console.error('🚨 Error en envío masivo:', error);
    throw error;
  }
};`,
        python: `# Envío masivo de facturas
import requests
import uuid

def enviar_lote_facturas(documentos):
    lote = {
        'operacion': 'generar_comprobantes',
        'documentos': documentos,
        'config': {
            'modo': 'produccion',
            'reintentos': 3,
            'notificacion_webhook': True
        }
    }
    
    headers = {
        'Authorization': 'Bearer sk_live_tu_api_key_2025',
        'X-Batch-ID': str(uuid.uuid4()),
        'Content-Type': 'application/json'
    }
    
    response = requests.post(
        'https://api.tudominio.com/v1/envio-masivo',
        json=lote,
        headers=headers
    )
    return response.json()`
      },
      responseTime: '2-5s',
      complexity: 'Advanced',
      successRate: '98.7%',

      testing: {
        method: 'POST',
        endpoint: '/v1/envio-masivo',
        sampleRequest: {
          operacion: 'generar_comprobantes',
          documentos: [
            {
              serie: 'F001',
              numero: '00001234',
              fecha: '2025-01-15',
              cliente: {
                ruc: '20512345678',
                razonSocial: 'EMPRESA EJEMPLO SAC'
              },
              total: 840.50
            }
          ],
          config: {
            modo: 'produccion',
            reintentos: 3,
            notificacion_webhook: true
          }
        }
      }

    }

  };




  const colors = {
    emerald: {
      bg: 'bg-emerald-500',
      gradient: 'from-emerald-400 to-teal-500',
      light: 'bg-emerald-50',
      text: 'text-emerald-700',
      border: 'border-emerald-200',
      glow: 'shadow-emerald-200',
      dark: 'bg-emerald-600'
    },
    blue: {
      bg: 'bg-blue-500',
      gradient: 'from-blue-400 to-cyan-500',
      light: 'bg-blue-50',
      text: 'text-blue-700',
      border: 'border-blue-200',
      glow: 'shadow-blue-200',
      dark: 'bg-blue-600'
    },
    purple: {
      bg: 'bg-purple-500',
      gradient: 'from-purple-400 to-indigo-500',
      light: 'bg-purple-50',
      text: 'text-purple-700',
      border: 'border-purple-200',
      glow: 'shadow-purple-200',
      dark: 'bg-purple-600'
    },
    orange: {
      bg: 'bg-orange-500',
      gradient: 'from-orange-400 to-amber-500',
      light: 'bg-orange-50',
      text: 'text-orange-700',
      border: 'border-orange-200',
      glow: 'shadow-orange-200',
      dark: 'bg-orange-600'
    }
  };

  const sections = [
    { id: 'implementation', name: 'Implementación', icon: Code },
    { id: 'parameters', name: 'Parámetros', icon: Settings },
    { id: 'response', name: 'Respuesta', icon: FileCode },
    { id: 'testing', name: 'Testing', icon: TestTube },
    { id: 'examples', name: 'Ejemplos', icon: BookText }
  ];

  const viewModes = [
    { id: 'documentation', name: 'Documentación', icon: BookOpen },
    { id: 'testing', name: 'Testing', icon: TestTube },
    { id: 'monitoring', name: 'Monitoreo', icon: BarChart3 }
  ];

  const currentDoc = docs[activeTab];
  const DocIcon = currentDoc.icon;
  const colorScheme = colors[currentDoc.color];
  const SectionIcon = sections.find(s => s.id === activeSection)?.icon || Code;
  const LanguageIcon = languages[activeLanguage]?.icon || FileJson;

  const filteredDocs = Object.entries(docs).filter(([key, doc]) =>
    doc.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    doc.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
    doc.category.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Componente para el dashboard de monitoreo
  const MonitoringDashboard = () => (
    <div className="space-y-6">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          { label: 'Requests/Second', value: monitoringData.requestsPerSecond, icon: TrendingUp, color: 'blue' },
          { label: 'Success Rate', value: monitoringData.successRate.toFixed(2) + '%', icon: Shield, color: 'green' },
          { label: 'Active Connections', value: monitoringData.activeConnections, icon: Users, color: 'purple' },
          { label: 'Avg Response Time', value: monitoringData.averageResponseTime + 'ms', icon: Clock, color: 'orange' }
        ].map((metric, index) => (
          <motion.div
            key={index}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
            className={`p-4 sm:p-6 rounded-2xl border backdrop-blur-sm ${darkMode ? 'bg-gray-800/50 border-gray-700' : 'bg-white/50 border-blue-200/30'
              }`}
          >
            <div className="flex items-center justify-between mb-2">
              <metric.icon className={`w-6 h-6 sm:w-8 sm:h-8 ${metric.color === 'blue' ? 'text-blue-500' :
                metric.color === 'green' ? 'text-green-500' :
                  metric.color === 'purple' ? 'text-purple-500' : 'text-orange-500'
                }`} />
              <div className={`w-2 h-2 rounded-full ${metric.color === 'blue' ? 'bg-blue-500' :
                metric.color === 'green' ? 'bg-green-500' :
                  metric.color === 'purple' ? 'bg-purple-500' : 'bg-orange-500'
                }`} />
            </div>
            <div className={`text-xl sm:text-2xl font-bold ${darkMode ? 'text-white' : 'text-slate-900'
              }`}>
              {metric.value}
            </div>
            <div className={`text-xs sm:text-sm ${darkMode ? 'text-gray-400' : 'text-slate-600'
              }`}>
              {metric.label}
            </div>
          </motion.div>
        ))}
      </div>

      {/* Gráfico simulado */}
      <div className={`rounded-2xl p-4 sm:p-6 border ${darkMode ? 'bg-gray-800/50 border-gray-700' : 'bg-white/50 border-blue-200/30'
        }`}>
        <h4 className={`text-lg font-semibold mb-4 ${darkMode ? 'text-white' : 'text-slate-900'
          }`}>Activity Timeline</h4>
        <div className="h-32 sm:h-48 flex items-end space-x-1">
          {[...Array(24)].map((_, i) => (
            <motion.div
              key={i}
              className={`flex-1 rounded-t ${darkMode ? 'bg-blue-500/50' : 'bg-blue-500/30'
                }`}
              style={{
                height: `${30 + Math.random() * 70}%`
              }}
              animate={{
                height: `${30 + Math.random() * 70}%`
              }}
              transition={{
                duration: 2,
                repeat: Infinity,
                repeatType: "reverse",
                delay: i * 0.1
              }}
            />
          ))}
        </div>
      </div>
    </div>
  );

  return (
    <div className={`min-h-screen transition-colors duration-300 ${darkMode
      ? 'bg-gradient-to-br from-gray-900 via-slate-900 to-blue-900'
      : 'bg-gradient-to-br from-slate-50 via-blue-50 to-cyan-50'
      }`}>
      {/* Efectos de fondo avanzados */}
      <div className="absolute inset-0 overflow-hidden">
        {/* Partículas animadas */}
        <div className="absolute inset-0">
          {[...Array(20)].map((_, i) => (
            <motion.div
              key={i}
              className={`absolute rounded-full ${darkMode ? 'bg-blue-500/10' : 'bg-blue-300/20'
                }`}
              style={{
                width: Math.random() * 8 + 2,
                height: Math.random() * 8 + 2,
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
              }}
              animate={{
                y: [0, -30, 0],
                opacity: [0, 1, 0],
              }}
              transition={{
                duration: Math.random() * 3 + 2,
                repeat: Infinity,
                delay: Math.random() * 2,
              }}
            />
          ))}
        </div>

        {/* Gradientes animados */}
        <motion.div
          className="absolute top-0 left-0 w-64 h-64 sm:w-96 sm:h-96 bg-gradient-to-r from-blue-200/40 to-cyan-200/30 rounded-full blur-3xl"
          animate={{
            x: [0, 100, 0],
            y: [0, -50, 0],
            scale: [1, 1.1, 1],
          }}
          transition={{
            duration: 20,
            repeat: Infinity,
            ease: "easeInOut"
          }}
        />
        <motion.div
          className="absolute bottom-0 right-0 w-64 h-64 sm:w-96 sm:h-96 bg-gradient-to-r from-emerald-200/30 to-teal-200/40 rounded-full blur-3xl"
          animate={{
            x: [0, -100, 0],
            y: [0, 50, 0],
            scale: [1, 1.2, 1],
          }}
          transition={{
            duration: 25,
            repeat: Infinity,
            ease: "easeInOut",
            delay: 2
          }}
        />
      </div>

      {/* Mobile Header - Solo más compacto en móviles */}
      <div className="lg:hidden fixed top-0 left-0 right-0 z-50">
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className={`p-3 border-b backdrop-blur-lg ${darkMode
            ? 'bg-gray-800/95 border-gray-700'
            : 'bg-white/95 border-blue-200/50'
            }`}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <button
                onClick={() => setSidebarOpen(true)}
                className={`p-2 rounded-lg ${darkMode ? 'hover:bg-gray-700' : 'hover:bg-slate-100'
                  }`}
              >
                <Menu className={`w-5 h-5 ${darkMode ? 'text-gray-300' : 'text-slate-600'}`} />
              </button>
              <div className="flex items-center space-x-2">
                <Terminal className={`w-5 h-5 ${darkMode ? 'text-blue-400' : 'text-blue-600'}`} />
                <span className={`font-semibold text-sm ${darkMode ? 'text-white' : 'text-slate-900'}`}>
                  API Docs
                </span>
              </div>
            </div>
            <div className="flex items-center space-x-1">
              <button
                onClick={() => setDarkMode(!darkMode)}
                className={`p-2 rounded-lg ${darkMode ? 'hover:bg-gray-700' : 'hover:bg-slate-100'
                  }`}
              >
                {darkMode ? <Eye className="w-4 h-4 text-gray-300" /> : <EyeOff className="w-4 h-4 text-slate-600" />}
              </button>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Contenedor principal - Solo ajuste de padding top para móviles */}
      <div className="relative z-10 container mx-auto px-4 lg:px-6 max-w-8xl pt-16 lg:pt-8 pb-8 lg:py-12">

        {/* Header premium - Mantenemos igual para desktop */}
        <motion.div
          className="text-center mb-8 lg:mb-16"
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.2 }}
            viewport={{ once: true }}
            className="inline-flex items-center space-x-2 bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm border border-blue-200/50 dark:border-blue-600/30 rounded-2xl px-4 py-2 mt-6 lg:mt-10 mb-6 shadow-lg shadow-blue-100/50 dark:shadow-blue-900/20"
          >
            <Sparkles className="w-4 h-4 text-blue-600 dark:text-blue-400" />
            <span className="text-xs font-mono bg-gradient-to-r from-blue-600 to-cyan-600 dark:from-blue-400 dark:to-cyan-400 bg-clip-text text-transparent">
              API_DOCS_V3.2025
            </span>
            <div className="w-1.5 h-1.5 bg-green-500 rounded-full animate-pulse" />
          </motion.div>

          <motion.h1
            className="text-2xl sm:text-4xl lg:text-6xl font-bold mb-4 lg:mb-6"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            viewport={{ once: true }}
          >
            <span className={darkMode ? "text-white" : "text-slate-900"}>Documentación</span>
            <span className="block bg-gradient-to-r from-blue-600 via-cyan-600 to-teal-600 dark:from-blue-400 dark:via-cyan-400 dark:to-teal-400 bg-clip-text text-transparent text-xl sm:text-4xl lg:text-6xl">
              Interactiva
            </span>
          </motion.h1>

          <motion.p
            className={`text-sm sm:text-lg max-w-2xl mx-auto leading-relaxed mb-6 lg:mb-8 ${darkMode ? "text-gray-300" : "text-slate-600"
              }`}
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ delay: 0.5 }}
            viewport={{ once: true }}
          >
            Explora, prueba y monitorea nuestros endpoints en tiempo real
          </motion.p>

          {/* Stats en tiempo real - Mantenemos igual */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.7 }}
            viewport={{ once: true }}
            className="grid grid-cols-2 lg:grid-cols-4 gap-3 max-w-4xl mx-auto"
          >
            {[
              { icon: Zap, label: 'Respuesta', value: monitoringData.averageResponseTime + 'ms' },
              { icon: Shield, label: 'Uptime', value: monitoringData.successRate.toFixed(2) + '%' },
              { icon: Users, label: 'Conexiones', value: monitoringData.activeConnections },
              { icon: TrendingUp, label: 'Req/seg', value: monitoringData.requestsPerSecond }
            ].map((stat, index) => (
              <motion.div
                key={index}
                whileHover={{ scale: 1.05, y: -2 }}
                className={`p-3 rounded-2xl backdrop-blur-sm border ${darkMode
                  ? 'bg-gray-800/50 border-gray-700'
                  : 'bg-white/50 border-blue-200/30'
                  } shadow-lg`}
              >
                <stat.icon className={`w-5 h-5 mx-auto mb-1 ${darkMode ? 'text-blue-400' : 'text-blue-600'
                  }`} />
                <div className={`text-lg font-bold ${darkMode ? 'text-white' : 'text-slate-900'
                  }`}>{stat.value}</div>
                <div className={`text-xs ${darkMode ? 'text-gray-400' : 'text-slate-600'
                  }`}>{stat.label}</div>
              </motion.div>
            ))}
          </motion.div>
        </motion.div>

        {/* Modos de vista - Mantenemos igual */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          className="flex justify-center mb-6 lg:mb-8"
        >
          <div className={`inline-flex rounded-2xl p-1 ${darkMode ? 'bg-gray-800' : 'bg-white/80'
            } backdrop-blur-sm border ${darkMode ? 'border-gray-700' : 'border-blue-200/50'
            } shadow-lg`}>
            {viewModes.map((mode) => (
              <button
                key={mode.id}
                onClick={() => setViewMode(mode.id)}
                className={`flex items-center space-x-1 px-3 py-2 rounded-xl transition-all text-sm ${viewMode === mode.id
                  ? `${colorScheme.light} ${colorScheme.text} shadow-sm`
                  : darkMode
                    ? 'text-gray-400 hover:text-white hover:bg-gray-700'
                    : 'text-slate-600 hover:text-slate-900 hover:bg-slate-100'
                  }`}
              >
                <mode.icon className="w-3 h-3 sm:w-4 sm:h-4" />
                <span className="font-medium">{mode.name}</span>
              </button>
            ))}
          </div>
        </motion.div>

        <div className="grid lg:grid-cols-4 gap-4 lg:gap-6">

          {/* Navegación lateral - Solo más compacta en móviles */}
          <AnimatePresence>
            {sidebarOpen && (
              <>
                {/* Backdrop */}
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="fixed inset-0 bg-black/50 z-40 lg:hidden"
                  onClick={() => setSidebarOpen(false)}
                />

                {/* Sidebar móvil - Solo más compacta en móviles */}
                <motion.div
                  initial={{ x: -300, opacity: 0 }}
                  animate={{ x: 0, opacity: 1 }}
                  exit={{ x: -300, opacity: 0 }}
                  transition={{ type: "spring", stiffness: 300, damping: 30 }}
                  className={`fixed left-0 top-0 h-full w-80 z-50 lg:hidden overflow-y-auto ${darkMode
                    ? 'bg-gray-800 border-r border-gray-700'
                    : 'bg-white border-r border-blue-200/50'
                    }`}
                >
                  <div className="p-4 h-full flex flex-col">
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center space-x-3">
                        <Terminal className={`w-6 h-6 ${darkMode ? 'text-blue-400' : 'text-blue-600'}`} />
                        <h3 className={`font-semibold text-lg ${darkMode ? 'text-white' : 'text-slate-900'}`}>Endpoints</h3>
                      </div>
                      <button
                        onClick={() => setSidebarOpen(false)}
                        className={`p-2 rounded-lg ${darkMode ? 'hover:bg-gray-700' : 'hover:bg-slate-100'}`}
                      >
                        <X className={`w-5 h-5 ${darkMode ? 'text-gray-400' : 'text-slate-600'}`} />
                      </button>
                    </div>

                    {/* Búsqueda - Texto más pequeño solo en móviles */}
                    <div className="relative mb-4">
                      <Search className={`absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 ${darkMode ? 'text-gray-400' : 'text-slate-400'}`} />
                      <input
                        type="text"
                        placeholder="Buscar endpoints..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className={`w-full pl-10 pr-4 py-2 rounded-xl border transition-all focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm ${darkMode
                          ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400'
                          : 'bg-white border-slate-200 text-slate-900 placeholder-slate-400'
                          }`}
                      />
                    </div>

                    {/* Lista de endpoints - Padding reducido solo en móviles */}
                    <div className="space-y-2 flex-1 overflow-y-auto">
                      {filteredDocs.map(([key, doc]) => {
                        const Icon = doc.icon;
                        const scheme = colors[doc.color];
                        const isActive = activeTab === key;

                        return (
                          <motion.button
                            key={key}
                            onClick={() => {
                              setActiveTab(key);
                              setSidebarOpen(false);
                            }}
                            className={`w-full p-3 rounded-xl text-left transition-all duration-300 group ${isActive
                              ? `border-2 shadow-lg ${scheme.border} ${scheme.glow} ${darkMode ? 'bg-gray-700' : 'bg-white'}`
                              : `${darkMode ? 'bg-gray-800/50 hover:bg-gray-700 border-gray-600' : 'bg-white/50 hover:bg-white border-gray-200/50'} border shadow-sm hover:shadow-md`
                              }`}
                            whileHover={{ scale: 1.02 }}
                            whileTap={{ scale: 0.98 }}
                          >
                            <div className="flex items-start space-x-3">
                              <div className={`p-2 rounded-lg ${isActive
                                ? scheme.light
                                : darkMode
                                  ? 'bg-gray-700 group-hover:bg-gray-600'
                                  : 'bg-gray-100 group-hover:bg-blue-50'
                                }`}>
                                <Icon className={`w-4 h-4 ${isActive ? scheme.text : darkMode ? 'text-gray-400 group-hover:text-white' : 'text-gray-600 group-hover:text-blue-600'}`} />
                              </div>

                              <div className="flex-1 min-w-0">
                                <div className="flex items-start justify-between mb-1">
                                  <h4 className={`font-semibold text-sm ${isActive ? scheme.text : darkMode ? 'text-gray-300 group-hover:text-white' : 'text-slate-800 group-hover:text-slate-900'}`}>
                                    {doc.title}
                                  </h4>
                                  <span className={`text-xs font-mono px-1 rounded ${darkMode ? 'bg-gray-700 text-gray-400' : 'bg-slate-100 text-slate-400'}`}>
                                    {doc.version}
                                  </span>
                                </div>
                                <p className={`text-xs leading-relaxed line-clamp-2 mb-2 ${darkMode ? 'text-gray-400 group-hover:text-gray-300' : 'text-gray-600 group-hover:text-gray-700'}`}>
                                  {doc.description}
                                </p>

                                <div className="flex items-center space-x-2">
                                  <span className={`text-xs px-2 py-1 rounded-full ${darkMode ? 'bg-gray-700 text-gray-300' : 'bg-slate-100 text-slate-700'}`}>
                                    {doc.category}
                                  </span>
                                </div>
                              </div>
                            </div>
                          </motion.button>
                        );
                      })}
                    </div>
                  </div>
                </motion.div>
              </>
            )}
          </AnimatePresence>

          {/* Navegación lateral Desktop - MANTENEMOS IGUAL */}
          <div className="hidden lg:block lg:col-span-1">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className={`rounded-3xl border p-6 sticky top-6 max-h-[85vh] overflow-y-auto shadow-xl backdrop-blur-lg custom-scrollbar ${darkMode
                ? 'bg-gray-800/80 border-gray-700 shadow-gray-900/30'
                : 'bg-white/80 border-blue-200/50 shadow-blue-100/30'
                }`}
            >
              {/* Header de navegación - ORIGINAL */}
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center space-x-3">
                  <Terminal className={`w-6 h-6 ${darkMode ? 'text-blue-400' : 'text-blue-600'}`} />
                  <h3 className={`font-semibold text-lg ${darkMode ? 'text-white' : 'text-slate-900'}`}>Endpoints</h3>
                  <span className={`px-2 py-1 rounded text-xs font-mono ${darkMode ? 'bg-blue-900/50 text-blue-300' : 'bg-blue-100 text-blue-700'}`}>
                    {Object.keys(docs).length}
                  </span>
                </div>
              </div>

              {/* Búsqueda avanzada - ORIGINAL */}
              <div className="relative mb-4">
                <Search className={`absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 ${darkMode ? 'text-gray-400' : 'text-slate-400'}`} />
                <input
                  type="text"
                  placeholder="Buscar endpoints..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className={`w-full pl-10 pr-4 py-2 rounded-xl border transition-all focus:ring-2 focus:ring-blue-500 focus:border-transparent ${darkMode
                    ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400'
                    : 'bg-white border-slate-200 text-slate-900 placeholder-slate-400'
                    }`}
                />
              </div>

              {/* Lista de endpoints con estados - ORIGINAL */}
              <div className="space-y-3">
                {filteredDocs.map(([key, doc]) => {
                  const Icon = doc.icon;
                  const scheme = colors[doc.color];
                  const isActive = activeTab === key;
                  const isHovered = hoveredTab === key;

                  return (
                    <motion.button
                      key={key}
                      onMouseEnter={() => setHoveredTab(key)}
                      onMouseLeave={() => setHoveredTab(null)}
                      onClick={() => setActiveTab(key)}
                      className={`w-full p-4 rounded-xl text-left transition-all duration-300 group relative overflow-hidden ${isActive
                        ? `border-2 shadow-lg ${scheme.border} ${scheme.glow} ${darkMode ? 'bg-gray-700' : 'bg-white'}`
                        : `${darkMode ? 'bg-gray-800/50 hover:bg-gray-700 border-gray-600' : 'bg-white/50 hover:bg-white border-gray-200/50'} border shadow-sm hover:shadow-md`
                        }`}
                      whileHover={{ scale: 1.02, y: -2 }}
                      whileTap={{ scale: 0.98 }}
                    >
                      <div className="flex items-start space-x-3 relative z-10">
                        <div className={`p-2 rounded-lg ${isActive
                          ? scheme.light
                          : darkMode
                            ? 'bg-gray-700 group-hover:bg-gray-600'
                            : 'bg-gray-100 group-hover:bg-blue-50'
                          }`}>
                          <Icon className={`w-4 h-4 ${isActive ? scheme.text : darkMode ? 'text-gray-400 group-hover:text-white' : 'text-gray-600 group-hover:text-blue-600'}`} />
                        </div>

                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between mb-1">
                            <h4 className={`font-semibold text-sm sm:text-base ${isActive ? scheme.text : darkMode ? 'text-gray-300 group-hover:text-white' : 'text-slate-800 group-hover:text-slate-900'}`}>
                              {doc.title}
                            </h4>
                            <span className={`text-xs font-mono px-1 rounded ${darkMode ? 'bg-gray-700 text-gray-400' : 'bg-slate-100 text-slate-400'}`}>
                              {doc.version}
                            </span>
                          </div>
                          <p className={`text-xs leading-relaxed line-clamp-2 mb-2 ${darkMode ? 'text-gray-400 group-hover:text-gray-300' : 'text-gray-600 group-hover:text-gray-700'}`}>
                            {doc.description}
                          </p>

                          <div className="flex items-center space-x-2">
                            <span className={`text-xs px-2 py-1 rounded-full ${darkMode ? 'bg-gray-700 text-gray-300' : 'bg-slate-100 text-slate-700'}`}>
                              {doc.category}
                            </span>
                            <span className={`text-xs px-2 py-1 rounded-full ${doc.complexity === 'Beginner'
                              ? darkMode ? 'bg-green-900/50 text-green-300' : 'bg-green-100 text-green-700'
                              : doc.complexity === 'Intermediate'
                                ? darkMode ? 'bg-yellow-900/50 text-yellow-300' : 'bg-yellow-100 text-yellow-700'
                                : darkMode ? 'bg-red-900/50 text-red-300' : 'bg-red-100 text-red-700'
                              }`}>
                              {doc.complexity}
                            </span>
                          </div>
                        </div>

                        <ChevronRight className={`w-4 h-4 mt-1 flex-shrink-0 transition-transform ${isActive ? 'rotate-90 ' + scheme.text : darkMode ? 'text-gray-500 group-hover:text-gray-400' : 'text-gray-400 group-hover:text-blue-500'}`} />
                      </div>
                    </motion.button>
                  );
                })}
              </div>

              {/* Quick actions - ORIGINAL */}
              <div className="mt-6 pt-6 border-t border-gray-200/50 dark:border-gray-700 space-y-3">
                <button className={`flex items-center space-x-2 w-full p-3 rounded-xl transition-colors group ${darkMode
                  ? 'bg-blue-900/30 hover:bg-blue-800/50 border border-blue-700/30'
                  : 'bg-blue-50 hover:bg-blue-100 border border-blue-200'
                  }`}>
                  <Rocket className={`w-4 h-4 ${darkMode ? 'text-blue-400' : 'text-blue-600'}`} />
                  <span className={`text-sm ${darkMode ? 'text-blue-300' : 'text-blue-700'}`}>Guía de inicio rápido</span>
                </button>
                <button className={`flex items-center space-x-2 w-full p-3 rounded-xl transition-colors group ${darkMode
                  ? 'bg-gray-700 hover:bg-gray-600 border border-gray-600'
                  : 'bg-slate-100 hover:bg-slate-200 border border-slate-200'
                  }`}>
                  <MessageCircle className={`w-4 h-4 ${darkMode ? 'text-gray-400' : 'text-slate-600'}`} />
                  <span className={`text-sm ${darkMode ? 'text-gray-300' : 'text-slate-700'}`}>Soporte técnico 24/7</span>
                </button>
              </div>
            </motion.div>
          </div>

          {/* Contenido principal */}
          <div className="lg:col-span-3">
            <AnimatePresence mode="wait">
              <motion.div
                key={`${activeTab}-${viewMode}`}
                initial={{ opacity: 0, x: 20, scale: 0.98 }}
                animate={{ opacity: 1, x: 0, scale: 1 }}
                exit={{ opacity: 0, x: -20, scale: 0.98 }}
                transition={{ type: "spring", stiffness: 300, damping: 30 }}
                className={`rounded-3xl border overflow-hidden h-full shadow-xl backdrop-blur-lg ${darkMode
                  ? 'bg-gray-800/80 border-gray-700 shadow-gray-900/30'
                  : 'bg-white/80 border-blue-200/50 shadow-blue-100/30'
                  }`}
              >
                {viewMode === 'documentation' && (

                  <div className="p-4 sm:p-6 lg:p-8 space-y-6 lg:space-y-8">
                    {/* Header del contenido */}
                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.1 }}
                      className="flex flex-col lg:flex-row lg:items-start justify-between gap-4 lg:gap-6"
                    >
                      <div className="flex items-start space-x-3 lg:space-x-4 flex-1">
                        <div className={`p-2 lg:p-3 rounded-xl lg:rounded-2xl ${colorScheme.light} shadow-lg`}>
                          <DocIcon className={`w-6 h-6 lg:w-8 lg:h-8 ${colorScheme.text}`} />
                        </div>
                        <div className="flex-1">
                          <div className="flex flex-col sm:flex-row sm:items-center space-y-2 sm:space-y-0 sm:space-x-3 mb-2">
                            <h3 className={`text-xl sm:text-2xl lg:text-3xl font-bold ${darkMode ? 'text-white' : 'text-slate-900'}`}>
                              {currentDoc.title}
                            </h3>
                            <div className="flex items-center space-x-2">
                              <span className={`px-2 py-1 ${colorScheme.light} ${colorScheme.text} rounded-lg text-xs lg:text-sm font-medium`}>
                                {currentDoc.version}
                              </span>
                              <span className={`px-2 py-1 rounded-lg text-xs lg:text-sm font-medium ${currentDoc.status === 'stable'
                                ? 'bg-green-100 text-green-700 dark:bg-green-900/50 dark:text-green-300'
                                : 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/50 dark:text-yellow-300'
                                }`}>
                                {currentDoc.status}
                              </span>
                            </div>
                          </div>
                          <p className={`text-sm lg:text-lg mb-3 lg:mb-4 ${darkMode ? 'text-gray-300' : 'text-slate-600'}`}>
                            {currentDoc.description}
                          </p>
                          <div className="flex flex-wrap gap-1 lg:gap-2">
                            <span className={`text-xs px-2 lg:px-3 py-1 rounded-full ${darkMode ? 'bg-gray-700 text-gray-300' : 'bg-slate-100 text-slate-700'}`}>
                              {currentDoc.category}
                            </span>
                            <span className="text-xs px-2 lg:px-3 py-1 bg-green-100 text-green-700 dark:bg-green-900/50 dark:text-green-300 rounded-full">
                              Actualizado: {currentDoc.lastUpdated}
                            </span>
                            <span className={`text-xs px-2 lg:px-3 py-1 rounded-full ${darkMode ? 'bg-blue-900/50 text-blue-300' : 'bg-blue-100 text-blue-700'}`}>
                              Uso: {currentDoc.usage}
                            </span>
                          </div>
                        </div>
                      </div>
                      <div className="flex flex-col sm:flex-row lg:flex-col gap-2 lg:gap-3">
                        <motion.button
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                          className="flex items-center justify-center space-x-2 px-3 lg:px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors shadow-lg shadow-blue-200 dark:shadow-blue-900/30 text-sm lg:text-base"
                        >
                          <Star className="w-3 h-3 lg:w-4 lg:h-4" />
                          <span>Favorito</span>
                        </motion.button>
                        <motion.button
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                          className={`flex items-center justify-center space-x-2 px-3 lg:px-4 py-2 rounded-lg transition-colors border text-sm lg:text-base ${darkMode
                            ? 'bg-gray-700 border-gray-600 text-gray-300 hover:bg-gray-600'
                            : 'bg-white border-slate-200 text-slate-700 hover:bg-slate-50'
                            }`}
                        >
                          <Share2 className="w-3 h-3 lg:w-4 lg:h-4" />
                          <span>Compartir</span>
                        </motion.button>
                      </div>
                    </motion.div>

                    {/* Selector de lenguajes */}
                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.15 }}
                      className="flex flex-col sm:flex-row sm:items-center justify-between gap-3"
                    >
                      <div className="flex flex-col sm:flex-row sm:items-center space-y-2 sm:space-y-0 sm:space-x-4">
                        <h4 className={`text-base lg:text-lg font-semibold ${darkMode ? 'text-white' : 'text-slate-900'}`}>
                          Lenguaje de Programación
                        </h4>
                        <div className="flex flex-wrap gap-1">
                          {Object.entries(languages).map(([key, lang]) => {
                            const LangIcon = lang.icon;
                            return (
                              <button
                                key={key}
                                onClick={() => setActiveLanguage(key)}
                                className={`flex items-center space-x-1 lg:space-x-2 px-2 lg:px-3 py-1 lg:py-2 rounded-lg transition-all text-xs lg:text-sm ${activeLanguage === key
                                  ? `${colorScheme.light} ${colorScheme.text} shadow-sm`
                                  : darkMode
                                    ? 'text-gray-400 hover:text-white hover:bg-gray-700'
                                    : 'text-slate-600 hover:text-slate-900 hover:bg-slate-100'
                                  }`}
                              >
                                <LangIcon className="w-3 h-3 lg:w-4 lg:h-4" />
                                <span className="font-medium">{lang.name}</span>
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    </motion.div>

                    {/* Navegación de secciones */}
                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.2 }}
                      className="flex overflow-x-auto pb-2 space-x-1 custom-scrollbar"
                    >
                      {sections.map((section) => {
                        const Icon = section.icon;
                        const isActive = activeSection === section.id;
                        return (
                          <button
                            key={section.id}
                            onClick={() => setActiveSection(section.id)}
                            className={`flex items-center space-x-1 lg:space-x-2 px-3 lg:px-4 py-2 rounded-xl transition-all whitespace-nowrap text-xs lg:text-sm ${isActive
                              ? `${colorScheme.light} ${colorScheme.text} shadow-sm`
                              : darkMode
                                ? 'text-gray-400 hover:text-white hover:bg-gray-700'
                                : 'text-slate-600 hover:text-slate-900 hover:bg-slate-100'
                              }`}
                          >
                            <Icon className="w-3 h-3 lg:w-4 lg:h-4" />
                            <span className="font-medium">{section.name}</span>
                          </button>
                        );
                      })}
                    </motion.div>

                    {/* Contenido de secciones */}
                    <AnimatePresence mode="wait">
                      <motion.div
                        key={activeSection}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -20 }}
                        transition={{ duration: 0.3 }}
                      >
                        {activeSection === 'implementation' && (
                          <div className="space-y-4 lg:space-y-6">
                            <div>
                              <h4 className={`text-base lg:text-lg font-semibold mb-3 lg:mb-4 flex items-center space-x-2 ${darkMode ? 'text-white' : 'text-slate-900'}`}>
                                <Layers className="w-4 h-4 lg:w-5 lg:h-5 text-blue-600" />
                                <span>Pasos de Implementación</span>
                              </h4>
                              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 lg:gap-4">
                                {currentDoc.steps.map((step, index) => (
                                  <motion.div
                                    key={index}
                                    initial={{ opacity: 0, x: 20 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    transition={{ delay: 0.3 + index * 0.1 }}
                                    className={`flex items-center space-x-2 lg:space-x-3 p-3 lg:p-4 rounded-xl border shadow-sm hover:shadow-md transition-all duration-300 group ${darkMode
                                      ? 'bg-gray-700 border-gray-600 hover:border-blue-500/30'
                                      : 'bg-white border-gray-200/50 hover:border-blue-200'
                                      }`}
                                  >
                                    <div className={`flex-shrink-0 w-6 h-6 lg:w-8 lg:h-8 ${colorScheme.light} ${colorScheme.text} rounded-lg flex items-center justify-center text-xs lg:text-sm font-bold group-hover:scale-110 transition-transform shadow-inner`}>
                                      {index + 1}
                                    </div>
                                    <span className={`text-xs lg:text-sm leading-relaxed ${darkMode ? 'text-gray-300' : 'text-slate-700'}`}>
                                      {step}
                                    </span>
                                  </motion.div>
                                ))}
                              </div>
                            </div>
                            <div className="space-y-3 lg:space-y-4">
                              <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-3">
                                <div className="flex items-center space-x-2 lg:space-x-3">
                                  <LanguageIcon className="w-5 h-5 lg:w-6 lg:h-6 text-blue-600" />
                                  <h4 className={`text-lg lg:text-xl font-semibold ${darkMode ? 'text-white' : 'text-slate-900'}`}>
                                    Ejemplo en {languages[activeLanguage].name}
                                  </h4>
                                </div>
                                <div className="flex flex-wrap gap-2">
                                  <motion.button
                                    onClick={() => runCodeExample(activeTab)}
                                    className="flex items-center space-x-1 lg:space-x-2 px-3 lg:px-4 py-1 lg:py-2 bg-green-100 text-green-700 dark:bg-green-900/50 dark:text-green-300 rounded-lg hover:bg-green-200 dark:hover:bg-green-800/50 transition-colors border border-green-200 dark:border-green-800 shadow-sm text-xs lg:text-sm"
                                    whileHover={{ scale: 1.05, y: -1 }}
                                    whileTap={{ scale: 0.95 }}
                                  >
                                    <Play className="w-3 h-3 lg:w-4 lg:h-4" />
                                    <span>Ejecutar</span>
                                  </motion.button>
                                  <motion.button
                                    onClick={() => copyToClipboard(currentDoc.codeExamples[activeLanguage], activeTab)}
                                    className={`flex items-center space-x-1 lg:space-x-2 px-3 lg:px-4 py-1 lg:py-2 rounded-lg transition-colors border shadow-sm text-xs lg:text-sm ${darkMode
                                      ? 'bg-gray-700 border-gray-600 text-gray-300 hover:bg-gray-600'
                                      : 'bg-slate-100 border-slate-200 text-slate-700 hover:bg-slate-200'
                                      }`}
                                    whileHover={{ scale: 1.05, y: -1 }}
                                    whileTap={{ scale: 0.95 }}
                                  >
                                    {copiedCode === activeTab ? (
                                      <>
                                        <Check className="w-3 h-3 lg:w-4 lg:h-4" />
                                        <span>Copiado!</span>
                                      </>
                                    ) : (
                                      <>
                                        <Copy className="w-3 h-3 lg:w-4 lg:h-4" />
                                        <span>Copiar</span>
                                      </>
                                    )}
                                  </motion.button>
                                </div>
                              </div>
                              <div className={`rounded-xl lg:rounded-2xl p-4 lg:p-6 border overflow-x-auto ${darkMode ? 'bg-gray-900 border-gray-700' : 'bg-slate-900 border-slate-800'}`}>
                                <pre className="text-xs sm:text-sm lg:text-base text-slate-200 font-mono leading-relaxed whitespace-pre-wrap break-words">
                                  <code>{currentDoc.codeExamples[activeLanguage]}</code>
                                </pre>
                              </div>
                            </div>
                          </div>
                        )}

                        {activeSection === 'testing' && (
                          <div className="space-y-6 lg:space-y-8">
                            {/* Título */}
                            <motion.div
                              initial={{ opacity: 0, y: 20 }}
                              animate={{ opacity: 1, y: 0 }}
                              className="flex items-center space-x-3"
                            >
                              <TestTube className="w-6 h-6 text-blue-600" />
                              <h4 className={`text-xl lg:text-2xl font-bold ${darkMode ? 'text-white' : 'text-slate-900'}`}>
                                Testing del Endpoint
                              </h4>
                            </motion.div>

                            {/* Contenido condicional */}
                            {currentDoc.testing ? (
                              <motion.div
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: 0.1 }}
                                className={`grid grid-cols-1 lg:grid-cols-2 gap-6 rounded-3xl border p-6 shadow-xl backdrop-blur-lg ${darkMode
                                  ? 'bg-gray-800/70 border-gray-700'
                                  : 'bg-white/70 border-blue-200/40'
                                  }`}
                              >
                                {/* Request */}
                                <div className="flex flex-col">
                                  <h5 className={`text-lg font-semibold mb-3 ${darkMode ? 'text-white' : 'text-slate-900'}`}>Request</h5>
                                  <div className={`flex-1 p-4 rounded-2xl border ${darkMode ? 'bg-gray-900 border-gray-700' : 'bg-slate-100 border-slate-200'}`}>
                                    <div className="flex items-center space-x-3 mb-3">
                                      <span className={`px-3 py-1 rounded-full text-xs font-mono ${darkMode ? 'bg-green-900/50 text-green-300' : 'bg-green-100 text-green-700'}`}>
                                        {currentDoc.testing.method}
                                      </span>
                                      <code className={`text-sm ${darkMode ? 'text-gray-300' : 'text-slate-600'}`}>
                                        {currentDoc.testing.endpoint}
                                      </code>
                                    </div>
                                    <pre className={`text-sm font-mono leading-relaxed ${darkMode ? 'text-gray-300' : 'text-slate-800'} overflow-x-auto`}>
                                      {JSON.stringify(currentDoc.testing.sampleRequest, null, 2)}
                                    </pre>
                                  </div>
                                </div>

                                {/* Response */}
                                <div className="flex flex-col">
                                  <h5 className={`text-lg font-semibold mb-3 ${darkMode ? 'text-white' : 'text-slate-900'}`}>Response</h5>
                                  <div className={`flex-1 p-4 rounded-2xl border ${darkMode ? 'bg-gray-900 border-gray-700' : 'bg-slate-100 border-slate-200'}`}>
                                    {testResult ? (
                                      <>
                                        <div className="flex items-center space-x-3 mb-3">
                                          <span className={`px-3 py-1 rounded-full text-xs font-mono ${testResult.status === 200
                                            ? 'bg-green-100 text-green-700 dark:bg-green-900/50 dark:text-green-300'
                                            : 'bg-red-100 text-red-700 dark:bg-red-900/50 dark:text-red-300'
                                            }`}>
                                            {testResult.status} {testResult.status === 200 ? 'OK' : 'ERROR'}
                                          </span>
                                          <code className={`text-sm ${darkMode ? 'text-gray-300' : 'text-slate-600'}`}>
                                            {testResult.responseTime}
                                          </code>
                                        </div>
                                        <pre className={`text-sm font-mono leading-relaxed ${darkMode ? 'text-gray-300' : 'text-slate-800'} overflow-x-auto`}>
                                          {JSON.stringify(testResult.data, null, 2)}
                                        </pre>
                                      </>
                                    ) : (
                                      <div className={`flex-1 flex flex-col items-center justify-center text-center ${darkMode ? 'text-gray-400' : 'text-slate-500'}`}>
                                        <TestTube className="w-10 h-10 mb-3 opacity-50" />
                                        <p className="text-sm">Ejecuta el test para ver los resultados</p>
                                      </div>
                                    )}
                                  </div>
                                </div>
                              </motion.div>
                            ) : (
                              <motion.div
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: 0.1 }}
                                className={`text-center py-12 rounded-3xl border ${darkMode ? 'bg-gray-800/70 border-gray-700' : 'bg-white/70 border-blue-200/40'}`}
                              >
                                <TestTube className="w-12 h-12 mx-auto mb-4 opacity-40" />
                                <h5 className={`text-lg font-semibold mb-2 ${darkMode ? 'text-gray-300' : 'text-slate-700'}`}>
                                  Testing no disponible
                                </h5>
                                <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-slate-500'}`}>
                                  Este endpoint aún no tiene configuración de testing.
                                </p>
                              </motion.div>
                            )}

                            {/* Botón Ejecutar Testing */}
                            {currentDoc.testing && (
                              <motion.div className="flex justify-center">
                                <motion.button
                                  onClick={() => testEndpoint(activeTab)}
                                  disabled={isTesting}
                                  className={`px-6 py-3 rounded-2xl font-semibold text-base transition-all flex items-center space-x-3 shadow-lg ${isTesting
                                    ? 'bg-gray-400 cursor-not-allowed'
                                    : 'bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-700 hover:to-cyan-700 text-white'
                                    }`}
                                  whileHover={!isTesting ? { scale: 1.05 } : {}}
                                  whileTap={!isTesting ? { scale: 0.95 } : {}}
                                >
                                  {isTesting ? (
                                    <>
                                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                      <span>Ejecutando Test...</span>
                                    </>
                                  ) : (
                                    <>
                                      <Play className="w-5 h-5" />
                                      <span>Ejecutar Test</span>
                                    </>
                                  )}
                                </motion.button>
                              </motion.div>
                            )}
                          </div>
                        )}


                      </motion.div>
                    </AnimatePresence>

                    {/* Información adicional */}
                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.5 }}
                      className="grid grid-cols-2 lg:grid-cols-4 gap-3 lg:gap-4 pt-4 lg:pt-6 border-t border-gray-200/50 dark:border-gray-700"
                    >
                      {[
                        { icon: Clock, label: 'Response Time', value: currentDoc.responseTime },
                        { icon: GitBranch, label: 'Complexity', value: currentDoc.complexity },
                        { icon: Server, label: 'Success Rate', value: currentDoc.successRate },
                        { icon: TrendingUp, label: 'Monthly Usage', value: currentDoc.usage }
                      ].map((stat, index) => (
                        <div
                          key={index}
                          className={`flex items-center space-x-2 lg:space-x-3 p-3 lg:p-4 rounded-xl border shadow-sm ${darkMode
                            ? 'bg-gray-700 border-gray-600'
                            : 'bg-white border-gray-200/50'
                            }`}
                        >
                          <stat.icon className={`w-4 h-4 lg:w-5 lg:h-5 ${index === 0 ? 'text-blue-600' :
                            index === 1 ? 'text-green-600' :
                              index === 2 ? 'text-purple-600' : 'text-orange-600'
                            }`} />
                          <div>
                            <p className={`text-xs lg:text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                              {stat.label}
                            </p>
                            <p className={`font-semibold text-sm lg:text-base ${darkMode ? 'text-white' : 'text-slate-900'}`}>
                              {stat.value}
                            </p>
                          </div>
                        </div>
                      ))}
                    </motion.div>
                  </div>
                )}

                {viewMode === 'monitoring' && (
                  <div className="p-4 sm:p-6 lg:p-8">
                    <h3 className={`text-xl lg:text-2xl font-bold mb-4 lg:mb-6 ${darkMode ? 'text-white' : 'text-slate-900'}`}>
                      Monitoreo en Tiempo Real.
                    </h3>
                    <MonitoringDashboard />
                  </div>
                )}

                {viewMode === 'testing' && (
                  <div className="p-4 sm:p-6 lg:p-8 space-y-8">
                    {/* Header */}
                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="flex items-center justify-between"
                    >
                      <div className="flex items-center space-x-4">
                        <div className={`p-3 rounded-2xl ${colorScheme.light} shadow-lg`}>
                          <DocIcon className={`w-7 h-7 ${colorScheme.text}`} />
                        </div>
                        <div>
                          <h3 className={`text-2xl lg:text-3xl font-bold ${darkMode ? 'text-white' : 'text-slate-900'}`}>
                            Testing ‑ {currentDoc.title}
                          </h3>
                          <p className={`text-sm lg:text-base ${darkMode ? 'text-gray-300' : 'text-slate-600'}`}>
                            Simula llamadas en tiempo real al endpoint
                          </p>
                        </div>
                      </div>
                      <span className={`px-3 py-1 rounded-full text-sm font-medium ${colorScheme.light} ${colorScheme.text}`}>
                        {currentDoc.version}
                      </span>
                    </motion.div>

                    {/* Contenido */}
                    {currentDoc.testing ? (
                      <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.1 }}
                        className={`grid grid-cols-1 lg:grid-cols-2 gap-6 rounded-3xl border p-6 shadow-xl backdrop-blur-lg ${darkMode
                          ? 'bg-gray-800/70 border-gray-700'
                          : 'bg-white/70 border-blue-200/40'
                          }`}
                      >
                        {/* Request */}
                        <div className="flex flex-col">
                          <h5 className={`text-lg font-semibold mb-3 ${darkMode ? 'text-white' : 'text-slate-900'}`}>Request</h5>
                          <div className={`flex-1 p-4 rounded-2xl border ${darkMode ? 'bg-gray-900 border-gray-700' : 'bg-slate-100 border-slate-200'}`}>
                            <div className="flex items-center space-x-3 mb-3">
                              <span className={`px-3 py-1 rounded-full text-xs font-mono ${darkMode ? 'bg-green-900/50 text-green-300' : 'bg-green-100 text-green-700'}`}>
                                {currentDoc.testing.method}
                              </span>
                              <code className={`text-sm ${darkMode ? 'text-gray-300' : 'text-slate-600'}`}>
                                {currentDoc.testing.endpoint}
                              </code>
                            </div>
                            <pre className={`text-sm font-mono leading-relaxed ${darkMode ? 'text-gray-300' : 'text-slate-800'} overflow-x-auto`}>
                              {JSON.stringify(currentDoc.testing.sampleRequest, null, 2)}
                            </pre>
                          </div>
                        </div>

                        {/* Response */}
                        <div className="flex flex-col">
                          <h5 className={`text-lg font-semibold mb-3 ${darkMode ? 'text-white' : 'text-slate-900'}`}>Response</h5>
                          <div className={`flex-1 p-4 rounded-2xl border ${darkMode ? 'bg-gray-900 border-gray-700' : 'bg-slate-100 border-slate-200'}`}>
                            {testResult ? (
                              <>
                                <div className="flex items-center space-x-3 mb-3">
                                  <span className={`px-3 py-1 rounded-full text-xs font-mono ${testResult.status === 200
                                    ? 'bg-green-100 text-green-700 dark:bg-green-900/50 dark:text-green-300'
                                    : 'bg-red-100 text-red-700 dark:bg-red-900/50 dark:text-red-300'
                                    }`}>
                                    {testResult.status} {testResult.status === 200 ? 'OK' : 'ERROR'}
                                  </span>
                                  <code className={`text-sm ${darkMode ? 'text-gray-300' : 'text-slate-600'}`}>
                                    {testResult.responseTime}
                                  </code>
                                </div>
                                <pre className={`text-sm font-mono leading-relaxed ${darkMode ? 'text-gray-300' : 'text-slate-800'} overflow-x-auto`}>
                                  {JSON.stringify(testResult.data, null, 2)}
                                </pre>
                              </>
                            ) : (
                              <div className={`flex-1 flex flex-col items-center justify-center text-center ${darkMode ? 'text-gray-400' : 'text-slate-500'}`}>
                                <TestTube className="w-10 h-10 mb-3 opacity-50" />
                                <p className="text-sm">Ejecuta el test para ver los resultados</p>
                              </div>
                            )}
                          </div>
                        </div>
                      </motion.div>
                    ) : (
                      <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.1 }}
                        className={`text-center py-12 rounded-3xl border ${darkMode ? 'bg-gray-800/70 border-gray-700' : 'bg-white/70 border-blue-200/40'}`}
                      >
                        <TestTube className="w-12 h-12 mx-auto mb-4 opacity-40" />
                        <h5 className={`text-lg font-semibold mb-2 ${darkMode ? 'text-gray-300' : 'text-slate-700'}`}>
                          Testing no disponible
                        </h5>
                        <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-slate-500'}`}>
                          Este endpoint aún no tiene configuración de testing.
                        </p>
                      </motion.div>
                    )}

                    {/* Botón Ejecutar Testing */}
                    {currentDoc.testing && (
                      <motion.div className="flex justify-center">
                        <motion.button
                          onClick={() => testEndpoint(activeTab)}
                          disabled={isTesting}
                          className={`px-6 py-3 rounded-2xl font-semibold text-base transition-all flex items-center space-x-3 shadow-lg ${isTesting
                            ? 'bg-gray-400 cursor-not-allowed'
                            : 'bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-700 hover:to-cyan-700 text-white'
                            }`}
                          whileHover={!isTesting ? { scale: 1.05 } : {}}
                          whileTap={!isTesting ? { scale: 0.95 } : {}}
                        >
                          {isTesting ? (
                            <>
                              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                              <span>Ejecutando Test...</span>
                            </>
                          ) : (
                            <>
                              <Play className="w-5 h-5" />
                              <span>Ejecutar Test</span>
                            </>
                          )}
                        </motion.button>
                      </motion.div>
                    )}
                  </div>
                )}
              </motion.div>
            </AnimatePresence>
          </div>


        </div>
      </div>



    </div>
  );
};

export default Documentation;